import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import api from "../../utils/axiosConfig";

export const useGroupUpload = ({ id, media, setMedia, fetchGroup, resetFaceMatch }) => {
  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [timeLeft, setTimeLeft] = useState("");
  const [uploadSpeed, setUploadSpeed] = useState("");
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  // Upload Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadItems, setUploadItems] = useState([]);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [totalBatchSizeMB, setTotalBatchSizeMB] = useState("0");
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBatchBytes, setTotalBatchBytes] = useState(0);

  const timerRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const handleUpload = useCallback((eOrFiles) => {
    let files = [];
    if (eOrFiles.target && eOrFiles.target.files) {
      files = Array.from(eOrFiles.target.files);
    } else if (Array.isArray(eOrFiles)) {
      files = eOrFiles;
    } else {
      files = Array.from(eOrFiles);
    }
    if (!files.length) return;

    // Filter to only include image files
    const validImageExtensions = /\.(jpe?g|png|gif|webp|heic|heif|arw|cr2|cr3|nef|nrw|orf|rw2|raf|dng|raw)$/i;
    files = files.filter(
      (file) => file && (file.type?.startsWith("image/") || validImageExtensions.test(file.name || ""))
    );

    if (!files.length) {
      toast.warn("No valid image files found in folder");
      return;
    }

    let sumBytes = 0;
    const items = files.map((file, idx) => {
      const b = file.size || 0;
      sumBytes += b;
      return {
        id: idx,
        file: file,
        name: file.name,
        sizeMB: (b / (1024 * 1024)).toFixed(2),
        sizeBytes: b,
        progress: 0,
        status: "pending",
      };
    });

    setUploadItems(items);
    setTotalBatchBytes(sumBytes);
    setTotalBatchSizeMB((sumBytes / (1024 * 1024)).toFixed(2));
    setUploadedBytes(0);
    setUploadedCount(0);
    setTotalFiles(files.length);
    setUploadStatus("idle");
    setShowUploadModal(true);
  }, []);

  const startUploadProcess = useCallback(async () => {
    if (!uploadItems.length || uploadStatus === "uploading" || uploading) return;

    setUploadStatus("uploading");
    setUploading(true);
    setUploadedCount(0);
    setUploadSpeed("Calculating...");
    setTimeLeft("Calculating...");

    if (timerRef.current) clearInterval(timerRef.current);

    const fileBytesUploadedMap = new Map();
    let networkStartTime = null;
    const newUploads = [];
    let skippedCount = 0;
    let completedCount = 0;

    const existingFileKeys = new Set(
      media.map((m) => `${m.fileName}_${m.fileSize}`)
    );

    let currentIndex = 0;
    const MAX_CONCURRENT_UPLOADS = 4;

    timerRef.current = setInterval(() => {
      if (!isMounted.current) return;
      
      setUploadedCount(completedCount);

      let currentTotalBytes = 0;
      fileBytesUploadedMap.forEach((bytes) => {
        currentTotalBytes += bytes;
      });
      setUploadedBytes(currentTotalBytes);

      if (networkStartTime && currentTotalBytes > 0) {
        const elapsedSecs = (Date.now() - networkStartTime) / 1000;
        if (elapsedSecs > 0) {
          const bytesPerSec = currentTotalBytes / elapsedSecs;
          const mbPerSec = (bytesPerSec / (1024 * 1024)).toFixed(2);
          setUploadSpeed(`${mbPerSec} MB/s`);

          const remainingBytes = Math.max(0, totalBatchBytes - currentTotalBytes);
          if (remainingBytes <= 0) {
            setTimeLeft("Almost done...");
          } else {
            const remSecs = Math.ceil(remainingBytes / bytesPerSec);
            if (remSecs > 60) {
              const mins = Math.floor(remSecs / 60);
              const secs = remSecs % 60;
              setTimeLeft(`~${mins}m ${secs}s remaining`);
            } else {
              setTimeLeft(`~${remSecs}s remaining`);
            }
          }
        }
      }
    }, 300);

    const uploadWorker = async () => {
      while (currentIndex < uploadItems.length && isMounted.current) {
        const i = currentIndex++;
        const item = uploadItems[i];
        const file = item.file;
        const fileKey = `${file.name}_${file.size}`;

        if (existingFileKeys.has(fileKey)) {
          console.log(`⚡ Instant client-side skip: ${file.name}`);
          skippedCount++;
          fileBytesUploadedMap.set(item.id, file.size);
          setUploadItems((prev) =>
            prev.map((it) => (it.id === item.id ? { ...it, progress: 100, status: "skipped" } : it))
          );
          if (skippedCount % 50 === 0) await new Promise((r) => setTimeout(r, 0));
        } else {
          if (!networkStartTime) networkStartTime = Date.now();
          setUploadItems((prev) =>
            prev.map((it) => (it.id === item.id ? { ...it, status: "uploading" } : it))
          );

          const formData = new FormData();
          formData.append("file", file);

          try {
            const res = await api.post(`/media/upload/${id}`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: (progressEvent) => {
                if (!isMounted.current) return;
                if (progressEvent.total) {
                  const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                  fileBytesUploadedMap.set(item.id, progressEvent.loaded);
                  setUploadItems((prev) =>
                    prev.map((it) => (it.id === item.id ? { ...it, progress: percent } : it))
                  );
                }
              },
            });

            if (!isMounted.current) return;

            if (res.data.skipped) {
              skippedCount++;
              fileBytesUploadedMap.set(item.id, file.size);
              setUploadItems((prev) =>
                prev.map((it) => (it.id === item.id ? { ...it, progress: 100, status: "skipped" } : it))
              );
            } else if (res.data.media) {
              fileBytesUploadedMap.set(item.id, file.size);
              newUploads.push(res.data.media);
              existingFileKeys.add(`${res.data.media.fileName}_${res.data.media.fileSize}`);
              setUploadItems((prev) =>
                prev.map((it) => (it.id === item.id ? { ...it, progress: 100, status: "done" } : it))
              );
            }
          } catch (error) {
            if (!isMounted.current) return;
            console.error(`Upload failed for ${file.name}:`, error);
            setUploadItems((prev) =>
              prev.map((it) => (it.id === item.id ? { ...it, status: "error" } : it))
            );
          }
        }
        completedCount++;
      }
    };

    const workers = [];
    for (let w = 0; w < MAX_CONCURRENT_UPLOADS; w++) {
      workers.push(uploadWorker());
    }

    await Promise.all(workers);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!isMounted.current) return;

    setUploadedCount(completedCount);
    setUploadedBytes(totalBatchBytes);

    if (newUploads.length > 0) {
      setMedia((prev) => [...prev, ...newUploads]);
      resetFaceMatch();
    }

    setUploading(false);
    setUploadStatus("completed");
    fetchGroup();
    setTimeLeft("");
    setUploadSpeed("");

    if (skippedCount > 0 && newUploads.length > 0) {
      toast.info(`✅ Uploaded ${newUploads.length} new, ⚠️ Skipped ${skippedCount} duplicate(s)`);
    } else if (skippedCount > 0 && newUploads.length === 0) {
      toast.info(`⚠️ All ${skippedCount} selected photo(s) were already uploaded!`);
    } else {
      toast.success(`✅ ${newUploads.length} photo${newUploads.length !== 1 ? "s" : ""} uploaded!`);
    }
  }, [id, media, uploadItems, uploading, uploadStatus, setMedia, resetFaceMatch, fetchGroup, totalBatchBytes]);

  const uploadModalProps = {
    showUploadModal,
    setShowUploadModal,
    uploading,
    uploadStatus,
    uploadItems,
    uploadedCount,
    totalFiles,
    uploadSpeed,
    timeLeft,
    uploadedBytes,
    totalBatchBytes,
    totalBatchSizeMB,
    startUploadProcess,
  };

  return {
    isDraggingFiles,
    setIsDraggingFiles,
    handleUpload,
    uploadModalProps,
  };
};
